<template>
  <v-overlay
    v-model="dialogVisibility"
    :color="overlayColor"
    opacity="0.98"
    :style="{ top: `${appBarHeight}px`, height: `calc(100vh - ${appBarHeight}px)`, zIndex: overlayZIndex }"
  >
    <v-container @keydown.enter="confirm">
      <!-- Mensaje y campo OTP -->
      <v-row class="pa-10 text-center">
        <div v-if="error" class="col-sm-1 col-md-3"></div>
        <div v-if="error" class="col-sm-10 col-md-6">
          <v-alert class="mt-5 red" border="left" text>
            {{ errorMsg }}
          </v-alert>
        </div>
        <div class="col-12" :style="{ color: textColor }">
          <strong>{{ $t("and_we_are_almost_there") }}</strong>
          <p>{{ $t("we_sent_code_your_phone") }}</p>
          <p>{{ maskedPhone }}</p>
        </div>

        <div class="col-12 otp-row">
          <input
            type="text"
            id="custom-input"
            :placeholder="$t('enter_received_code')"
            v-model="otpCode"
            @input="otpCode = $event.target.value.toUpperCase()"
            class="txt-uppercase otp-input"
            v-uppercase
          />
        </div>

        <!-- Botón OK en su propia fila con más separación -->
        <div class="col-12 otp-btn-row">
          <v-btn
            class="footer-text-btn otp-ok-btn"
            :disabled="otpCode.length < 1"
            @click="confirm"
          >
            {{ $t("ok") }}
          </v-btn>
        </div>
      </v-row>

      <!-- Resend SMS -->
      <v-row v-if="showResendOtp" class="pa-10 text-center">
        <v-col md="12" :style="{ color: textColor }">
          <p>{{ $t("resend_sms_heading") }}</p>
          <v-btn class="footer-text-btn" @click="resendOtpCode">
            {{ $t("resend_sms") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-overlay>
</template>

<script>
export default {
  name: "sign-confirm-otp-modal",

  // Si usas v-model desde el padre, seguimos emitiendo update:dialog
  model: { prop: "dialog", event: "input" },

  props: {
    dialog: Boolean,
    error: Boolean,
    errorMsg: String,
    phoneNumber: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      otpCode: "",
      showResendOtp: false,
      dialogVisibility: false, // v-model local
      appBarHeight: 0,
      overlayZIndex: 1,
      resendTimerId: null // <--- renamed (no underscore)
    };
  },

  computed: {
    maskedPhone() {
      const digits = (this.phoneNumber || "").replace(/\D/g, "");
      if (!digits) return "";
      const last3 = digits.slice(-3);
      const masked = digits.slice(0, -3).replace(/\d/g, "X");
      return `${(masked.match(/.{1,3}/g) || []).join(" ")} ${last3}`.trim();
    },
    overlayColor() {
      return this.$store.state.common.footerColor;
    },
    textColor() {
      return this.$store.state.common.footerTextColor;
    }
  },

  watch: {
    // sincroniza prop -> local y dispara temporizador cuando se abre
    dialog: {
      immediate: true,
      handler(isOpen) {
        this.dialogVisibility = isOpen;
        // reset
        this.showResendOtp = false;
        if (this.resendTimerId) {
          clearTimeout(this.resendTimerId);
          this.resendTimerId = null;
        }
        if (isOpen) {
          // recalc height (por si cambió la appBar)
          this.computeAppBarHeight();
          // activar botón de reenvío tras 30s
          this.resendTimerId = setTimeout(() => {
            this.showResendOtp = true;
            this.resendTimerId = null;
          }, 30000);
        }
      }
    },

    // cuando cerramos localmente, informamos al padre
    dialogVisibility(val) {
      if (!val) {
        // emitimos evento compatible con v-model en el padre
        this.$emit("update:dialog", false);
      }
    }
  },

  mounted() {
    this.computeAppBarHeight();
    window.addEventListener("resize", this.computeAppBarHeight);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.computeAppBarHeight);
    if (this.resendTimerId) {
      clearTimeout(this.resendTimerId);
      this.resendTimerId = null;
    }
  },

  methods: {
    computeAppBarHeight() {
      // ajusta los selectores si tu appBar tiene otra clase/id
      const selectors = [".v-app-bar", ".app-bar", "header.v-toolbar", "header"];
      let el = null;
      for (const sel of selectors) {
        el = document.querySelector(sel);
        if (el) break;
      }
      const fallback = 64;
      const height = el ? Math.round(el.getBoundingClientRect().height) : fallback;
      this.appBarHeight = height;
    },

    confirm() {
      this.$emit("submitOtpCode", this.otpCode);
      // cerrar localmente (esto emitirá update:dialog)
      this.dialogVisibility = false;
    },

    resendOtpCode() {
      this.$emit("resendOtpCode");
      // opcional: reiniciar el contador y deshabilitar otra vez
      this.showResendOtp = false;
      if (this.resendTimerId) clearTimeout(this.resendTimerId);
      this.resendTimerId = setTimeout(() => {
        this.showResendOtp = true;
        this.resendTimerId = null;
      }, 30000);
    }
  }
};
</script>

<style scoped>
/* Centrar el input y reducir su anchura si quieres */
.otp-row {
  display: flex;
  justify-content: center;
  margin-top: 6px;
  margin-bottom: 6px;
}

/* Separación más notable entre input y botón */
.otp-btn-row {
  display: flex;
  justify-content: center;
  margin-top: 18px; /* <- ajusta este valor para aumentar/disminuir separación */
}

/* Opcional: hacer el botón algo más compacto en anchura */
.otp-ok-btn {
  min-width: 120px;
  padding-left: 18px;
  padding-right: 18px;
}

/* Ajuste rápido: ancho basado en caracteres del placeholder */
.otp-input {
  width: 26ch;       /* cambia 26 por el número de caracteres aproximado */
  max-width: 100%;
  box-sizing: border-box;
  text-align: center;
  padding: 6px 8px;
  height: 38px;
  font-size: 1rem;
  font-family: "Courier New", Courier, monospace;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.12);
  outline: none;
}

/* centrar placeholder también (por si el navegador no lo hereda) */
.otp-input::placeholder {
  text-align: center;
}
</style>
